import type { ComponentProps, ReactElement, ReactNode } from 'react'
import { createNonNullableContext, tv } from '@joyful/utils'
import type { VariantProps } from '@joyful/utils'
import { Slot } from '../Slot'

const emptyStateVariants = tv({
  base: 'flex flex-col items-center justify-center rounded-md p-4',

  // Floor when no parent height pins the card (stacked mobile / full-bleed).
  compoundVariants: [
    {
      class: {
        base: 'min-h-56',
      },
      grow: 'fill',
    },
    {
      class: {
        base: 'min-h-56',
      },
      variant: 'inset',
    },
  ],

  defaultVariants: {
    grow: 'auto',
    size: 'lg',
    variant: 'default',
  },

  slots: {
    icon: 'mx-auto text-tremor-content-subtle',
    subtitle: 'text-tremor-content text-balance text-center',
    title: 'font-medium text-center',
  },

  variants: {
    grow: {
      auto: {
        base: null,
      },
      fill: {
        base: 'flex-1',
      },
    },

    size: {
      lg: {
        base: 'min-h-60 gap-y-5',
        icon: 'size-8 md:size-10',
        subtitle: 'text-xs md:text-sm',
        title: 'mt-2 text-base md:text-lg',
      },
      md: {
        base: 'min-h-40 gap-y-3',
        icon: 'size-8 md:size-10',
        subtitle: 'text-xs md:text-sm',
        title: 'text-sm md:text-base',
      },
      sm: {
        base: 'min-h-16 gap-y-4',
        icon: 'size-6',
        subtitle: 'text-xs',
        title: 'text-sm text-tremor-content',
      },
      xs: {
        base: 'min-h-12 gap-2',
        icon: 'size-5',
        subtitle: 'text-xs',
        title: 'text-xs text-tremor-content',
      },
    },

    variant: {
      default: {
        base: null,
      },
      inset: {
        base: 'h-full flex-1 rounded-none bg-tremor-background-muted',
      },
      well: {
        base: 'border border-tremor-border bg-tremor-background-muted',
      },
    },
  },
})

const EmptyStateClassNamesContext = createNonNullableContext<
  ReturnType<typeof emptyStateVariants>
>('EmptyStateClassNamesContext')

export interface EmptyStateProps
  extends
    Omit<ComponentProps<'div'>, 'className'>,
    VariantProps<typeof emptyStateVariants> {
  actions?: ReactNode
  children: string | ReactElement
  icon?: ReactElement
  subtitle?: ReactNode
}

function EmptyStateTitle({
  children,
  ...props
}: Omit<ComponentProps<'p'>, 'className'>) {
  const classNames = EmptyStateClassNamesContext.useRequiredContext()

  return (
    <p className={classNames.title()} {...props}>
      {children}
    </p>
  )
}

export type EmptyStateSubtitleProps = Omit<ComponentProps<'p'>, 'className'>

export function EmptyStateSubtitle({
  children,
  ...props
}: EmptyStateSubtitleProps) {
  const classNames = EmptyStateClassNamesContext.useRequiredContext()
  return (
    <p className={classNames.subtitle()} {...props}>
      {children}
    </p>
  )
}

export function EmptyState({
  actions,
  children,
  grow,
  icon,
  size,
  subtitle,
  variant,
  ...rest
}: EmptyStateProps) {
  const classNames = emptyStateVariants({
    grow,
    size,
    variant,
  })

  return (
    <EmptyStateClassNamesContext.Context value={classNames}>
      <div className={classNames.base()} {...rest}>
        <div className="flex flex-col gap-y-1 text-center">
          {icon ? (
            <Slot className={classNames.icon()} aria-hidden>
              {icon}
            </Slot>
          ) : null}
          {typeof children === 'string' ? (
            <EmptyStateTitle>{children}</EmptyStateTitle>
          ) : (
            children
          )}
          {typeof subtitle === 'string' ? (
            <EmptyStateSubtitle>{subtitle}</EmptyStateSubtitle>
          ) : (
            subtitle
          )}
        </div>
        {actions}
      </div>
    </EmptyStateClassNamesContext.Context>
  )
}

export { emptyStateVariants }
